import { useUsersCurrentAccessesQuery } from '@/api/queries/users';
import { Grid, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useOperatorsRidesFilterQuery } from 'api/queries/operators';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { colors, DataTable, formatDateTimeLongWithLocale, UserPermission } from 'remote/global';
import { ROUTES } from 'utils/data/routes';
import { v4 as uuidv4 } from 'uuid';
import { ShowOrderDetailsModalLink } from '../LogisticsView/LogisticsAllRidesView';

const now = new Date();
const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
function DashboardUpcomingRidesTable() {
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo || {};
  const { data: allRides, isLoading } = useOperatorsRidesFilterQuery({
    pageNo: '0',
    from: startOfDay.toISOString(),
  });

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'bookingNumber',
        headerName: `${t('LogisticsView.rides.table.header.bookingNumber')}`,
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <div>
            {[UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
              permissions?.order as UserPermission
            ) ? (
              <Typography
                sx={{
                  color: colors.gray9,
                  fontSize: '14px',
                }}
              >
                #{row.bookingNumber}
              </Typography>
            ) : (
              <ShowOrderDetailsModalLink
                bookingNumber={row.bookingNumber}
                /* // TODO: Attach correct ride - currently BE does not send rideNumber */
                rideNumber={1}
                text={`#${row.bookingNumber}`}
              />
            )}
          </div>
        ),
      },
      {
        field: 'estimatedStartAt',
        headerName: `${t('LogisticsView.rides.table.header.startedAtDate')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        valueFormatter: ({ value }) => formatDateTimeLongWithLocale(value, i18n.language),
      },
      {
        field: 'estimatedEndAt',
        headerName: `${t('LogisticsView.rides.table.header.estimatedEndAt')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        valueFormatter: ({ value }) => formatDateTimeLongWithLocale(value, i18n.language),
      },
      {
        field: 'clientStartAddress',
        headerName: `${t('LogisticsView.rides.table.header.clientStartAddress')}`,
        flex: 1,
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'clientEndAddress',
        headerName: `${t('LogisticsView.rides.table.header.clientEndAddress')}`,
        flex: 1,
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'clientName',
        headerName: `${t('LogisticsView.rides.table.header.clientName')}`,
        flex: 1,
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'passengerCount',
        headerName: `${t('LogisticsView.rides.table.header.passengerCount')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'licensePlate',
        headerName: `${t('LogisticsView.rides.table.header.licensePlate')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '14px',
              color: colors.headerBlack,
            }}
            component={Link}
            to={[ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET, row.vehicleId].join('/')}
          >
            {row.licensePlate}
          </Typography>
        ),
      },
      {
        field: 'seats',
        headerName: `${t('LogisticsView.rides.table.header.seats')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'driverName',
        headerName: `${t('LogisticsView.rides.table.header.driverName')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        valueFormatter: ({ value }) => value ?? '-',
      },
    ];
  }, [t, permissions?.order, i18n.language]);

  return (
    <Grid container spacing={4}>
      <Grid item sx={{ width: '100%' }}>
        <DataTable
          rows={allRides != null ? { ...allRides, elements: allRides.elements.slice(0, 5) } : []}
          columns={columns}
          loading={isLoading}
          getRowId={() => uuidv4()}
          noRowsText={t('DashboardView.analytics.noUpcomingRides').toString()}
          t={t}
        />
      </Grid>
    </Grid>
  );
}

export default DashboardUpcomingRidesTable;
